import type { ScopeCreateRequest, ScopeResponse } from '@ydistri/api-sdk';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useCreateScopeMutation,
  useGetScopeQuery,
  useGetScopesQuery,
  useGetScopeSkuCountQuery,
  useGetScopeUsagesQuery,
  useUpdateScopeMutation,
} from '../../../../apis/apiScopes';
import { formatScopeUsage } from '../../../../components/domain/Scopes/scopeUsageFormatting';
import { getRequestErrorMessage } from '../../../../lib/utils/getRequestErrorMessage';
import { useStableParams } from '../../../../routes/useStableParams';
import { useAsoScenarioStatus } from '../../hooks/useAsoScenarioStatus';
import type {
  AsoScopeAssignmentLabel,
  AsoScopeOption,
  AsoScopeSelectValue,
} from '../asoScopeAssignmentTypes';

interface CreatedScope {
  name: string;
  scopeId: number;
}

interface RecentlyCreatedScope extends CreatedScope {
  isArchived: boolean;
  isShared: boolean;
}

interface UseAsoScopeAssignmentProps {
  asoScenarioId: number;
  assignmentLabel: AsoScopeAssignmentLabel;
  isAssigning: boolean;
  onAssignScope: (scopeId: number | null) => Promise<void>;
  selectedScopeId?: number | null;
}

export const getSuggestedScopeName = (
  assignmentLabel: AsoScopeAssignmentLabel,
  asoScenarioId: number,
  scenarioName?: string,
): string => {
  const trimmedScenarioName = scenarioName?.trim();

  return `ASO ${assignmentLabel} - #${asoScenarioId}${
    trimmedScenarioName ? ` ${trimmedScenarioName}` : ''
  }`;
};

const toScopeCopyRequest = (scope: ScopeResponse): ScopeCreateRequest => ({
  name: `${scope.name} - copy`,
  isShared: false,
  sections: scope.sections.map(section => ({
    rules: section.rules.map(rule => ({
      ids: rule.ids,
      scopeSectionRuleModeId: rule.scopeSectionRuleModeId,
      scopeSectionRuleTypeId: rule.scopeSectionRuleTypeId,
    })),
  })),
});

export const useAsoScopeAssignment = ({
  asoScenarioId,
  assignmentLabel,
  isAssigning,
  onAssignScope,
  selectedScopeId,
}: UseAsoScopeAssignmentProps) => {
  const { asoRequestId, projectShortName } = useStableParams();
  const { isLocked, scenario } = useAsoScenarioStatus();
  const [displayedScopeId, setDisplayedScopeId] = useState(selectedScopeId);
  const [createdScope, setCreatedScope] = useState<CreatedScope>();
  const [recentlyCreatedScope, setRecentlyCreatedScope] = useState<RecentlyCreatedScope>();
  const [creationError, setCreationError] = useState<string>();
  const [assignmentError, setAssignmentError] = useState<string>();
  const [sharingError, setSharingError] = useState<string>();
  const hasSelectedScope = displayedScopeId !== null && displayedScopeId !== undefined;

  useEffect(() => {
    setDisplayedScopeId(selectedScopeId);
    setSharingError(undefined);
  }, [selectedScopeId]);

  const {
    data: scopes = [],
    isError: isScopesError,
    isFetching: isFetchingScopes,
  } = useGetScopesQuery(projectShortName ? { projectShortName } : skipToken);
  const {
    data: selectedScope,
    isError: isSelectedScopeError,
    isFetching: isFetchingSelectedScope,
  } = useGetScopeQuery(
    projectShortName && hasSelectedScope
      ? { projectShortName, scopeId: displayedScopeId }
      : skipToken,
  );
  const {
    data: resolvedSkuCount,
    isError: isSkuCountError,
    isFetching: isFetchingSkuCount,
  } = useGetScopeSkuCountQuery(displayedScopeId ?? skipToken);
  const {
    data: scopeUsages = [],
    isError: isScopeUsagesError,
    isFetching: isFetchingScopeUsages,
  } = useGetScopeUsagesQuery(
    projectShortName && hasSelectedScope
      ? { projectShortName, scopeId: displayedScopeId }
      : skipToken,
  );
  const [createScope, createState] = useCreateScopeMutation();
  const [updateScope, updateState] = useUpdateScopeMutation();

  const scopeOptions = useMemo<AsoScopeOption[]>(() => {
    const availableScopes = scopes
      .filter(scope => scope.isShared)
      .map(scope => ({
        isArchived: scope.isArchived,
        isShared: scope.isShared,
        name: scope.name,
        scopeId: scope.scopeId,
      }));

    if (hasSelectedScope && !availableScopes.some(scope => scope.scopeId === displayedScopeId)) {
      const listedScope = scopes.find(scope => scope.scopeId === displayedScopeId);
      let selectedSummary: RecentlyCreatedScope | undefined;
      if (selectedScope?.scopeId === displayedScopeId) {
        selectedSummary = {
          isArchived: selectedScope.isArchived,
          isShared: selectedScope.isShared,
          name: selectedScope.name,
          scopeId: selectedScope.scopeId,
        };
      } else if (listedScope) {
        selectedSummary = {
          isArchived: listedScope.isArchived,
          isShared: listedScope.isShared,
          name: listedScope.name,
          scopeId: listedScope.scopeId,
        };
      } else if (recentlyCreatedScope?.scopeId === displayedScopeId) {
        selectedSummary = recentlyCreatedScope;
      }

      if (selectedSummary) availableScopes.push(selectedSummary);
    }

    const options = availableScopes
      .map(scope => ({
        disabled: scope.isArchived && scope.scopeId !== displayedScopeId,
        label: `${scope.name}${scope.isArchived ? ' — archived' : ''}`,
        searchLabel: `${scope.name}${scope.isArchived ? ' — archived' : ''}`,
        shared: scope.isShared,
        value: scope.scopeId,
      }))
      .sort((left, right) => left.searchLabel.localeCompare(right.searchLabel));

    if (hasSelectedScope && !availableScopes.some(scope => scope.scopeId === displayedScopeId)) {
      const unavailableLabel = isFetchingSelectedScope
        ? 'Loading selected Scope…'
        : `Scope ${displayedScopeId} — unavailable`;

      options.push({
        disabled: true,
        label: unavailableLabel,
        searchLabel: unavailableLabel,
        shared: false,
        value: displayedScopeId,
      });
    }

    return options;
  }, [
    displayedScopeId,
    hasSelectedScope,
    isFetchingSelectedScope,
    recentlyCreatedScope,
    scopes,
    selectedScope,
  ]);

  const resetCreateState = useCallback(() => {
    setCreatedScope(undefined);
    setCreationError(undefined);
  }, []);

  const assignScope = useCallback(
    async (scopeId: number | null): Promise<boolean> => {
      setAssignmentError(undefined);

      try {
        await onAssignScope(scopeId);
        setDisplayedScopeId(scopeId);
        return true;
      } catch {
        setAssignmentError('The reusable Scope assignment could not be saved.');
        return false;
      }
    },
    [onAssignScope],
  );

  const changeSelection = useCallback(
    async (value?: AsoScopeSelectValue) => {
      setSharingError(undefined);
      resetCreateState();
      await assignScope(value ?? null);
    },
    [assignScope, resetCreateState],
  );

  const createAndAssign = useCallback(
    async (request: ScopeCreateRequest) => {
      setCreationError(undefined);
      setSharingError(undefined);

      let scopeToAssign = createdScope;
      if (!scopeToAssign) {
        try {
          const scope = await createScope(request).unwrap();
          scopeToAssign = { name: scope.name, scopeId: scope.scopeId };
          setCreatedScope(scopeToAssign);
          setRecentlyCreatedScope({
            isArchived: scope.isArchived,
            isShared: scope.isShared,
            name: scope.name,
            scopeId: scope.scopeId,
          });
        } catch (error) {
          setCreationError(
            getRequestErrorMessage(error, 'The reusable Scope could not be created.'),
          );
          return;
        }
      }

      if (!(await assignScope(scopeToAssign.scopeId))) {
        setCreationError(
          `Scope "${scopeToAssign.name}" was created, but could not be assigned. Retry the assignment.`,
        );
        return;
      }

      resetCreateState();
    },
    [assignScope, createScope, createdScope, resetCreateState],
  );

  const suggestedScopeName = getSuggestedScopeName(assignmentLabel, asoScenarioId, scenario?.name);

  const createAndAssignScope = useCallback(
    () => createAndAssign({ name: suggestedScopeName, isShared: false, sections: [] }),
    [createAndAssign, suggestedScopeName],
  );

  const makeScopeCopy = useCallback(() => {
    if (!selectedScope) return Promise.resolve();
    return createAndAssign(toScopeCopyRequest(selectedScope));
  }, [createAndAssign, selectedScope]);

  const toggleSelectedScopeSharing = useCallback(async () => {
    if (!selectedScope || selectedScope.scopeId !== displayedScopeId || selectedScope.isArchived) {
      return;
    }

    setSharingError(undefined);

    try {
      await updateScope({
        scopeId: selectedScope.scopeId,
        request: { isShared: !selectedScope.isShared },
      }).unwrap();
    } catch (error) {
      setSharingError(getRequestErrorMessage(error, 'The Scope sharing state could not be saved.'));
    }
  }, [displayedScopeId, selectedScope, updateScope]);

  const listedSelectedScope = scopes.find(scope => scope.scopeId === displayedScopeId);
  const selectedScopeSharingState =
    selectedScope && selectedScope.scopeId === displayedScopeId
      ? selectedScope.isShared
      : (listedSelectedScope?.isShared ??
        (recentlyCreatedScope && recentlyCreatedScope.scopeId === displayedScopeId
          ? recentlyCreatedScope.isShared
          : undefined));
  const hasSelectedScopeSharingState = hasSelectedScope && selectedScopeSharingState !== undefined;
  const isSelectedScopeShared = hasSelectedScopeSharingState && selectedScopeSharingState === true;
  const canToggleSelectedScopeSharing = Boolean(
    selectedScope && selectedScope.scopeId === displayedScopeId && !selectedScope.isArchived,
  );
  const externalScopeUsages = useMemo(
    () =>
      scopeUsages.filter(
        usage =>
          !(
            asoRequestId !== undefined &&
            usage.aso?.request.id === asoRequestId &&
            usage.aso.scenario.id === asoScenarioId
          ),
      ),
    [asoRequestId, asoScenarioId, scopeUsages],
  );
  const hasExternalScopeUsages = externalScopeUsages.length > 0;
  const isCreatingOrAssigning = createState.isLoading || isAssigning;
  const isUpdatingSharing = updateState.isLoading;
  const isReadOnly = isLocked || selectedScope?.isArchived === true;
  const hasZeroResolvedSkus = hasSelectedScope && !isFetchingSkuCount && resolvedSkuCount === 0;
  const selectedScopeUsages = useMemo(
    () =>
      externalScopeUsages.map(usage => ({
        key: `${String(usage.type)}-${usage.occurrenceId}`,
        label: formatScopeUsage(usage),
      })),
    [externalScopeUsages],
  );

  return {
    assignmentError,
    canToggleSelectedScopeSharing,
    changeSelection,
    createAndAssignScope,
    createdScope,
    creationError,
    displayedScopeId,
    hasSelectedScope,
    hasSelectedScopeSharingState,
    hasExternalScopeUsages,
    hasZeroResolvedSkus,
    isCreatingOrAssigning,
    isFetchingSkuCount,
    isFetchingScopes,
    isFetchingScopeUsages,
    isLocked,
    isReadOnly,
    isScopesError,
    isSelectedScopeError,
    isSelectedScopeShared,
    isSkuCountError,
    isScopeUsagesError,
    isUpdatingSharing,
    makeScopeCopy,
    resolvedSkuCount,
    scopeOptions,
    selectedScope,
    selectedScopeUsages,
    sharingError,
    suggestedScopeName,
    toggleSelectedScopeSharing,
  };
};
